/**
 * Real-time Market Price Service
 * Provides live price feeds for crypto, forex, and stocks
 * Uses polling with smart caching to stay within free tier limits
 */

import { marketAPI } from './api';

// Price update callbacks
const priceUpdateCallbacks = new Map();

// Price cache with timestamps
const priceCache = new Map();
const CACHE_TTL = 10000; // 10 seconds

// Polling intervals
let cryptoIntervalId = null;
let forexIntervalId = null;
let stocksIntervalId = null;

// Subscribed instruments
const subscriptions = {
  crypto: new Set(),
  forex: new Set(),
  stocks: new Set()
};

/**
 * Get cached price or null if expired
 */
const getCachedPrice = (symbol) => {
  const cached = priceCache.get(symbol);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  return null;
};

/**
 * Update cache
 */
const setCachedPrice = (symbol, data) => {
  priceCache.set(symbol, {
    data,
    timestamp: Date.now()
  });
};

/**
 * Notify all subscribers of a price update
 */
const notifySubscribers = (symbol, priceData) => {
  const callbacks = priceUpdateCallbacks.get(symbol);
  if (callbacks) {
    callbacks.forEach(callback => {
      try {
        callback(priceData);
      } catch (error) {
        console.error(`Error in price callback for ${symbol}:`, error);
      }
    });
  }
};

/**
 * Fetch crypto prices from backend
 */
const fetchCryptoPrices = async () => {
  if (subscriptions.crypto.size === 0) return;
  
  try {
    const data = await marketAPI.getCrypto();
    
    // Map common crypto symbols
    const cryptoMapping = {
      'BTCUSD': data.bitcoin,
      'ETHUSD': data.ethereum,
      'XRPUSD': data.ripple,
      'BNBUSD': data.binancecoin,
      'SOLUSD': data.solana,
      'ADAUSD': data.cardano,
      'DOGEUSD': data.dogecoin,
      'DOTUSD': data.polkadot,
      'MATICUSD': data.matic,
      'LTCUSD': data.litecoin
    };
    
    subscriptions.crypto.forEach(symbol => {
      const coinData = cryptoMapping[symbol];
      if (coinData) {
        const priceData = {
          symbol,
          bid: coinData.usd * 0.9999, // Simulated spread
          ask: coinData.usd * 1.0001,
          price: coinData.usd,
          change: coinData.usd_24h_change || 0,
          changePercent: coinData.usd_24h_change || 0,
          high: coinData.usd * 1.02,
          low: coinData.usd * 0.98,
          volume: coinData.usd_24h_vol || 0,
          timestamp: Date.now(),
          source: 'coingecko'
        };
        
        setCachedPrice(symbol, priceData);
        notifySubscribers(symbol, priceData);
      }
    });
  } catch (error) {
    console.error('Error fetching crypto prices:', error);
  }
};

/**
 * Fetch forex prices from backend
 */
const fetchForexPrices = async () => {
  if (subscriptions.forex.size === 0) return;
  
  try {
    const data = await marketAPI.getForex();
    
    subscriptions.forex.forEach(symbol => {
      const pair = symbol.replace('/', '');
      const forexData = data[pair];
      
      if (forexData) {
        const priceData = {
          symbol,
          bid: forexData.bid,
          ask: forexData.ask,
          price: (forexData.bid + forexData.ask) / 2,
          change: forexData.change || 0,
          changePercent: forexData.change_percent || 0,
          high: forexData.high || forexData.ask * 1.001,
          low: forexData.low || forexData.bid * 0.999,
          spread: Math.round((forexData.ask - forexData.bid) * 10000) / 10, // pips
          timestamp: Date.now(),
          source: 'forex'
        };
        
        setCachedPrice(symbol, priceData);
        notifySubscribers(symbol, priceData);
      }
    });
  } catch (error) {
    console.error('Error fetching forex prices:', error);
  }
};

/**
 * Fetch stock/BIST prices from backend
 */
const fetchStockPrices = async () => {
  if (subscriptions.stocks.size === 0) return;
  
  try {
    const data = await marketAPI.getBISTStocks();
    
    subscriptions.stocks.forEach(symbol => {
      // Clean symbol (remove -IST suffix for matching)
      const cleanSymbol = symbol.replace('-IST', '');
      const stockData = data[cleanSymbol] || data[symbol];
      
      if (stockData) {
        const priceData = {
          symbol,
          bid: stockData.price * 0.999,
          ask: stockData.price * 1.001,
          price: stockData.price,
          change: stockData.change || 0,
          changePercent: stockData.change_percent || 0,
          high: stockData.high || stockData.price * 1.02,
          low: stockData.low || stockData.price * 0.98,
          volume: stockData.volume || 0,
          timestamp: Date.now(),
          source: 'bist'
        };
        
        setCachedPrice(symbol, priceData);
        notifySubscribers(symbol, priceData);
      }
    });
  } catch (error) {
    console.error('Error fetching stock prices:', error);
  }
};

/**
 * Start price polling for a market type
 */
const startPolling = (marketType) => {
  switch (marketType) {
    case 'crypto':
      if (!cryptoIntervalId) {
        fetchCryptoPrices(); // Immediate fetch
        cryptoIntervalId = setInterval(fetchCryptoPrices, 30000); // 30 seconds
      }
      break;
    case 'forex':
      if (!forexIntervalId) {
        fetchForexPrices();
        forexIntervalId = setInterval(fetchForexPrices, 5000); // 5 seconds
      }
      break;
    case 'stocks':
      if (!stocksIntervalId) {
        fetchStockPrices();
        stocksIntervalId = setInterval(fetchStockPrices, 15000); // 15 seconds
      }
      break;
  }
};

/**
 * Stop price polling for a market type
 */
const stopPolling = (marketType) => {
  switch (marketType) {
    case 'crypto':
      if (cryptoIntervalId && subscriptions.crypto.size === 0) {
        clearInterval(cryptoIntervalId);
        cryptoIntervalId = null;
      }
      break;
    case 'forex':
      if (forexIntervalId && subscriptions.forex.size === 0) {
        clearInterval(forexIntervalId);
        forexIntervalId = null;
      }
      break;
    case 'stocks':
      if (stocksIntervalId && subscriptions.stocks.size === 0) {
        clearInterval(stocksIntervalId);
        stocksIntervalId = null;
      }
      break;
  }
};

/**
 * Determine market type from symbol
 */
const getMarketType = (symbol) => {
  const cryptoSymbols = ['BTCUSD', 'ETHUSD', 'XRPUSD', 'BNBUSD', 'SOLUSD', 'ADAUSD', 'DOGEUSD', 'DOTUSD', 'MATICUSD', 'LTCUSD'];
  const forexSymbols = ['EURUSD', 'GBPUSD', 'USDJPY', 'USDCHF', 'AUDUSD', 'NZDUSD', 'USDCAD', 'EURGBP', 'EURJPY', 'GBPJPY'];
  
  if (cryptoSymbols.includes(symbol)) return 'crypto';
  if (forexSymbols.includes(symbol) || symbol.includes('/')) return 'forex';
  if (symbol.includes('-IST') || symbol.includes('BIST')) return 'stocks';
  
  // Default to stocks for unknown symbols
  return 'stocks';
};

/**
 * Subscribe to price updates for a symbol
 * @param {string} symbol - The instrument symbol
 * @param {function} callback - Callback function (priceData) => void
 * @returns {function} Unsubscribe function
 */
export const subscribeToPriceUpdates = (symbol, callback) => {
  // Add to callbacks
  if (!priceUpdateCallbacks.has(symbol)) {
    priceUpdateCallbacks.set(symbol, new Set());
  }
  priceUpdateCallbacks.get(symbol).add(callback);
  
  // Add to subscriptions and start polling
  const marketType = getMarketType(symbol);
  subscriptions[marketType].add(symbol);
  startPolling(marketType);
  
  // Return cached price immediately if available
  const cached = getCachedPrice(symbol);
  if (cached) {
    setTimeout(() => callback(cached), 0);
  }
  
  // Return unsubscribe function
  return () => {
    const callbacks = priceUpdateCallbacks.get(symbol);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        priceUpdateCallbacks.delete(symbol);
        subscriptions[marketType].delete(symbol);
        stopPolling(marketType);
      }
    }
  };
};

/**
 * Get current price for a symbol (one-time fetch)
 */
export const getCurrentPrice = async (symbol) => {
  // Check cache first
  const cached = getCachedPrice(symbol);
  if (cached) return cached;
  
  // Fetch from appropriate market
  const marketType = getMarketType(symbol);
  
  switch (marketType) {
    case 'crypto':
      await fetchCryptoPrices();
      break;
    case 'forex':
      await fetchForexPrices();
      break;
    case 'stocks':
      await fetchStockPrices();
      break;
  }
  
  return getCachedPrice(symbol);
};

/**
 * Get multiple prices at once
 */
export const getMultiplePrices = async (symbols) => {
  const results = {};
  
  // Group by market type
  const grouped = {
    crypto: [],
    forex: [],
    stocks: []
  };
  
  symbols.forEach(symbol => {
    const type = getMarketType(symbol);
    grouped[type].push(symbol);
    subscriptions[type].add(symbol);
  });
  
  // Fetch all market types in parallel
  await Promise.all([
    grouped.crypto.length > 0 ? fetchCryptoPrices() : Promise.resolve(),
    grouped.forex.length > 0 ? fetchForexPrices() : Promise.resolve(),
    grouped.stocks.length > 0 ? fetchStockPrices() : Promise.resolve()
  ]);
  
  // Collect results
  symbols.forEach(symbol => {
    results[symbol] = getCachedPrice(symbol);
  });
  
  return results;
};

/**
 * Stop all price feeds
 */
export const stopAllPriceFeeds = () => {
  if (cryptoIntervalId) {
    clearInterval(cryptoIntervalId);
    cryptoIntervalId = null;
  }
  if (forexIntervalId) {
    clearInterval(forexIntervalId);
    forexIntervalId = null;
  }
  if (stocksIntervalId) {
    clearInterval(stocksIntervalId);
    stocksIntervalId = null;
  }
  
  subscriptions.crypto.clear();
  subscriptions.forex.clear();
  subscriptions.stocks.clear();
  priceUpdateCallbacks.clear();
  priceCache.clear();
};

// Export for use in components
export default {
  subscribeToPriceUpdates,
  getCurrentPrice,
  getMultiplePrices,
  stopAllPriceFeeds
};
